import BacktestingHistory from "features/bots/BotForm/Backtesting/BacktestingHistory.tsx";
import React, { useContext, useState } from "react";
import { BotSummaryTabs } from "@shared/features/bots/BotForm/common/types.ts";
import { SignalsBotSummaryTab } from "@shared/features/bots/types";
import Tabs from "common/Tabs/Tabs.tsx";
import styled, { css } from "styled-components";
import { formPanel, panelTabs } from "features/bots/BotForm/NewLook/styles";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import FormPublicBots from "features/bots/PublicBots/FormPublicBots.tsx";
import Title from "common/Typography/Title.tsx";
import { BOT_FORM_BOTTOM_SPACE } from "features/bots/common/constants.ts";
import useMeasure from "utils/hooks/useMeasure.ts";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface DcaBotSummaryTabsProps {
  isSmallScreen?: boolean;
  /** New look: rendered inside the form layout's helpers panel. */
  isNewLookPanel?: boolean;
}

const BotFormSummaryTabs = ({
  isSmallScreen,
  isNewLookPanel,
}: DcaBotSummaryTabsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { showBacktesting, showPublicBots } = useContext(BotFormContext)!;
  const [measureRef, { height: titleHeight }] = useMeasure<HTMLDivElement>();

  const [botSummaryTab, setBotSummaryTab] = useState(
    BotSummaryTabs.PUBLIC_BOTS
  );

  const botSummaryTabToComponent = {
    [BotSummaryTabs.BACKTESTING]: <BacktestingHistory />,
    [BotSummaryTabs.PUBLIC_BOTS]: <FormPublicBots />,
  };

  const botSummaryTabsItems = Object.values(BotSummaryTabs).map((tab) => {
    return {
      key: tab,
      label: t(tab),
      children: botSummaryTabToComponent[tab],
    };
  });

  const getContent = () => {
    if (showBacktesting && showPublicBots) {
      return (
        <StyledTabs
          activeKey={botSummaryTab}
          onChange={(tab: string) => setBotSummaryTab(tab as BotSummaryTabs)}
          items={botSummaryTabsItems}
        />
      );
    } else if (showBacktesting) {
      if (isNewLookPanel) {
        return (
          <>
            <PanelTitle>
              {t(SignalsBotSummaryTab.BACKTESTING_HISTORY)}
            </PanelTitle>
            <PanelContent>
              <BacktestingHistory />
            </PanelContent>
          </>
        );
      }
      return (
        <>
          <div ref={measureRef}>
            <Title level={3} style={{ padding: "16px 16px 0" }}>
              Backtesting History
            </Title>
          </div>
          <div style={{ height: `calc(100% - ${titleHeight}px)` }}>
            <BacktestingHistory />
          </div>
        </>
      );
    }
  };

  if (isNewLookPanel) {
    return <PanelWrapper>{getContent()}</PanelWrapper>;
  }

  return (
    <div>
      <Wrapper $isSmallScreen={isSmallScreen}>{getContent()}</Wrapper>
      <div style={{ height: BOT_FORM_BOTTOM_SPACE }} />
    </div>
  );
};

export default BotFormSummaryTabs;

const StyledTabs = styled(Tabs)`
  display: flex;
  flex-direction: column;
  height: 100%;
  &.ant-tabs-top > .ant-tabs-nav {
    padding: 0 12px;
    margin: 0 !important;
  }
  &.ant-tabs .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${panelTabs};
      &.ant-tabs-top > .ant-tabs-nav {
        padding: 0;
      }
      &.ant-tabs .ant-tabs-content-holder {
        flex: 1 1 auto;
        min-height: 0;
      }
    `};
`;
const Wrapper = styled.div<{ $isSmallScreen?: boolean }>`
  width: 365px;
  height: ${(p) =>
    p.$isSmallScreen ? "100%" : `calc(100% - ${BOT_FORM_BOTTOM_SPACE}px)`};
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  background: ${(p) => p.theme.colors.cardBgColor};
  ${(p) =>
    p.$isSmallScreen &&
    `
    min-height: 320px;
    max-height: 800px;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${formPanel};
      overflow: hidden;
    `};
`;
const PanelWrapper = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  > .ant-tabs {
    flex: 1 1 auto;
    min-height: 0;
  }
`;
const PanelTitle = styled.div`
  flex: none;
  padding: 16px 20px 0;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 15px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const PanelContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`;
